/**
 * Reading a time series pasted as text.
 *
 * - One line of numbers (`12, 15, 9, 20`) is a plain list of values.
 * - One value per line is a plain column.
 * - Two or more columns are read as *label, value*: a date, month or year in the first
 *   column and the measurement in the second. Further columns are ignored.
 * - A header row (the value cell is text) is skipped; blank and `NA` values are dropped
 *   and counted, because a gap in a moving average would silently shift every window.
 */

import { parseNumberInput, parseNumberList } from "@/lib/parse-number";
import {
  isMissingCell,
  parseCellRows,
} from "@/lib/tool-kit/stats-research/matrix";

export interface Series {
  values: number[];
  /** One label per value; "" where the series had no label column. */
  labels: string[];
  hasLabels: boolean;
  /** Rows dropped for a missing or unreadable value (header row not counted). */
  skipped: number;
  hasHeader: boolean;
}

export function parseSeries(text: string): Series {
  const { rows } = parseCellRows(text);
  const empty: Series = {
    values: [],
    labels: [],
    hasLabels: false,
    skipped: 0,
    hasHeader: false,
  };
  if (rows.length === 0) {
    return empty;
  }
  // A single line is a horizontal list of values.
  if (rows.length === 1) {
    const values = parseNumberList(text);
    return { ...empty, values, labels: values.map(() => "") };
  }
  const widest = Math.max(...rows.map((row) => row.length));
  const labelled = widest >= 2;
  const column = labelled ? 1 : 0;
  const first = rows[0][column] ?? "";
  const hasHeader = !isMissingCell(first) && parseNumberInput(first) === null;
  const body = hasHeader ? rows.slice(1) : rows;
  const values: number[] = [];
  const labels: string[] = [];
  let skipped = 0;
  for (const row of body) {
    const value = parseNumberInput(row[column] ?? "");
    if (value === null) {
      skipped += 1;
    } else {
      values.push(value);
      labels.push(labelled ? (row[0] ?? "") : "");
    }
  }
  return { values, labels, hasLabels: labelled, skipped, hasHeader };
}
